<template>
  <div class="block">
    <SectionHeading
      :title="roleView ? $t('Назначенные права') : $t('Настройка прав')"
      :description="
        !roleView
          ? $t('Выберите необходимые права для данной роли')
          : permissions.length === 0
          ? $t('У данной роли нет назначенных прав')
          : ''
      "
    />

    <template v-if="roleView">
      <ViewGroupList :groups="viewGroups" />
    </template>

    <div v-else class="w-full flex flex-col gap-4">
      <div
        v-for="(items, key) in grouped_permissions"
        :key="key"
        class="flex flex-col rounded-xl"
      >
        <!-- GROUP HEADER -->
        <div class="gray-block" @click="toggleExpand(key)">
          <Checkbox
            :model-value="isGroupChecked(items)"
            :indeterminate="isGroupIndeterminate(items)"
            :disabled="roleView"
            :class="{ readonly: roleView }"
            @change="toggleGroup(items)"
            @click.stop
          >
          </Checkbox>
          <span class="font-medium text-sm text-foreground line-clamp-2">
            {{ getGroupName(key) }}
          </span>

          <svg-icon
            :name="expandedGroups[key] ? 'huge-up' : 'huge-down'"
            class="icon"
          />
        </div>

        <transition name="slide-fade">
          <div
            v-show="expandedGroups[key]"
            class="flex flex-col mt-3 pl-8 gap-1"
          >
            <div class="flex flex-col gap-3">
              <Checkbox
                v-for="item in items"
                :key="item.code"
                :model-value="isPermissionChecked(item.code)"
                :disabled="roleView"
                class="flex items-center gap-3 cursor-pointer"
                :class="{ readonly: roleView }"
                @change="togglePermission(item.code)"
              >
                <span class="text-sm font-medium text-foreground">
                  {{ item.name + " " + item.code }}
                </span>
              </Checkbox>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { getPermissionGroupedList } from "@/entities/role";
import { getServiceCodesList } from "@/entities/template";
import { Checkbox } from "@/shared/ui/checkbox";
import { SectionHeading } from "@/shared/ui/section-heading";
import ViewGroupList from "@/shared/ui/custom/ViewGroupList.vue";
import { toRef } from "vue";
import { usePermissionGroups } from "@/shared/lib/composables/usePermissionGroups";

export default {
  name: "RoleSettingRight",
  components: { Checkbox, SectionHeading, ViewGroupList },

  props: {
    form: {
      type: Object,
      default: () => ({ permissions: [] }),
    },
  },

  setup(props) {
    const formRef = toRef(props, "form");
    const {
      expandedGroups,
      permissions,
      isGroupChecked,
      isGroupIndeterminate,
      toggleGroup,
      togglePermission,
      isPermissionChecked,
      toggleExpand,
      initExpandedGroups,
    } = usePermissionGroups(formRef);

    return {
      expandedGroups,
      permissions,
      isGroupChecked,
      isGroupIndeterminate,
      toggleGroup,
      togglePermission,
      isPermissionChecked,
      toggleExpand,
      initExpandedGroups,
    };
  },

  data() {
    return {
      grouped_permissions: {},
      serviceList: null,
    };
  },

  computed: {
    roleView() {
      return this.$route.name === "role_item_view";
    },
    viewGroups() {
      return Object.entries(this.grouped_permissions)
        .map(([key, items]) => ({
          title: this.getGroupName(key),
          items: items.filter((i) => this.permissions.includes(i.code)),
        }))
        .filter((g) => g.items.length > 0);
    },
  },

  mounted() {
    this.initData();
  },

  methods: {
    async initData() {
      await Promise.all([
        this.getServiceList(),
        this.getPermissionGroupedList(),
      ]);

      if (!Array.isArray(this.form.permissions)) {
        this.form.permissions = [];
      }
    },

    async getServiceList() {
      const data = await getServiceCodesList();
      if (data?.success && data.payload) {
        this.serviceList = data.payload;
      }
    },

    async getPermissionGroupedList() {
      const data = await getPermissionGroupedList();

      if (data?.success && data.payload) {
        this.grouped_permissions = data.payload;
        this.initExpandedGroups(this.grouped_permissions);
      }
    },

    getGroupName(code) {
      if (!this.serviceList) return code;
      const group = this.serviceList.find((g) => g.code === code);
      return group?.label || code;
    },

    validate() {
      return Promise.resolve(true);
    },
  },
};
</script>

<style scoped lang="scss">
.block {
  @apply w-1/2 h-full flex flex-col gap-6 p-8 overflow-auto rounded-2xl;

  background-color: hsl(var(--background));
  border: 1px solid hsl(var(--border));
}

.gray-block {
  @apply w-full flex items-center px-4 py-2 gap-3 rounded-xl cursor-pointer transition-colors;

  background-color: hsl(var(--muted));

  &:hover {
    background-color: hsl(var(--accent));
  }

  .icon {
    @apply w-6 h-6 ml-auto flex-shrink-0 transition-transform;
    color: hsl(var(--muted-foreground));
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.readonly {
  pointer-events: none;
}

@media (max-width: 1400px) {
  .block {
    @apply w-full;
  }
}
</style>
